import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { FileText } from 'lucide-react-native';
import { memo, useCallback, useMemo, useState } from 'react';
import { View } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { useMessageTemplates } from '@/components/chat/composer/useMessageTemplates';
import {
  Actionsheet,
  ActionsheetBackdrop,
  ActionsheetContent,
  ActionsheetDragIndicator,
  ActionsheetDragIndicatorWrapper,
  ActionsheetScrollView,
} from '@/components/ui/actionsheet';
import { Divider } from '@/components/ui/divider';
import { Icon } from '@/components/ui/icon';
import { Popover, PopoverBackdrop, PopoverBody, PopoverContent } from '@/components/ui/popover';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import {
  interpolateTemplate,
  isTemplateForAudience,
  type MessageTemplate,
  type MessageTemplateAudience,
  type MessageTemplateCategory,
  type TemplateVariables,
} from '@/constants/messageTemplates';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { ICON_BUTTON_HIT_SLOP } from '@/util/accessibleTouchTarget';

type TemplateSection = {
  title: string;
  data: MessageTemplate[];
};

function buildSections(
  templates: MessageTemplate[],
  categoryLabels: Record<MessageTemplateCategory, string>
): TemplateSection[] {
  const grouped = new Map<MessageTemplateCategory, MessageTemplate[]>();

  for (const template of templates) {
    const list = grouped.get(template.category) ?? [];

    list.push(template);

    grouped.set(template.category, list);
  }

  return Array.from(grouped.entries()).map(([category, templates]) => ({
    title: categoryLabels[category] ?? category,
    data: templates,
  }));
}

type TemplateActionProps = {
  /** Which participant the open thread is addressed to; only matching templates are listed. */
  audience: Exclude<MessageTemplateAudience, 'both'>;
  isSending: boolean;
  onInsert: (text: string) => void;
  onOpen?: () => void;
  onPress?: () => void;
  onSelect?: (template: MessageTemplate) => void;
  variables: TemplateVariables;
};

export const TemplateAction = memo(function TemplateAction({
  audience,
  isSending,
  onInsert,
  onOpen,
  onPress,
  onSelect,
  variables,
}: TemplateActionProps) {
  const [isOpen, setIsOpen] = useState(false);

  const { isEnabled, categories, templates } = useMessageTemplates();

  const sections = useMemo(
    () =>
      buildSections(
        templates.filter((template) => isTemplateForAudience(template, audience)),
        categories
      ),
    [audience, categories, templates]
  );

  const handleInsert = useCallback(
    (template: MessageTemplate) => {
      onSelect?.(template);

      onInsert(interpolateTemplate(template.body, variables));

      setIsOpen(false);
    },
    [onInsert, onSelect, variables]
  );

  if (!isEnabled || sections.length === 0) {
    return null;
  }

  const isDisabled = isSending;

  const trigger = (triggerProps?: Record<string, unknown>) => (
    <Pressable
      {...triggerProps}
      disabled={isDisabled}
      onPress={() => {
        const nextIsOpen = !isOpen;

        onPress?.();

        if (nextIsOpen) {
          onOpen?.();
        }

        setIsOpen(nextIsOpen);
      }}
      hitSlop={ICON_BUTTON_HIT_SLOP}
      className="h-10 w-10 items-center justify-center rounded-full active:opacity-70"
      {...getAccessibilityProps({ label: 'Message templates', role: 'button' })}>
      <Icon
        as={FileText}
        size="lg"
        className={twJoin(isDisabled && 'text-typography-300', !isDisabled && 'text-primary-500')}
      />
    </Pressable>
  );

  const templateList = (
    <>
      {sections.map((section, sectionIndex) => (
        <View key={section.title}>
          {sectionIndex > 0 && <Divider className="my-1.5" />}
          <Text className="mb-1 px-1 pt-1 font-semibold text-xs uppercase tracking-[0.08em] text-typography-600">
            {section.title}
          </Text>
          {section.data.map((template, templateIndex) => (
            <TemplateRow
              key={template.id}
              template={template}
              variables={variables}
              onInsert={handleInsert}
              isSending={isSending}
              isLastInSection={templateIndex === section.data.length - 1}
            />
          ))}
        </View>
      ))}
    </>
  );

  if (!isWeb) {
    return (
      <>
        {trigger()}
        <Actionsheet isOpen={isOpen} onClose={() => setIsOpen(false)}>
          <ActionsheetBackdrop />
          <ActionsheetContent className="max-h-[78vh] px-0 pb-2 pt-2">
            <ActionsheetDragIndicatorWrapper>
              <ActionsheetDragIndicator />
            </ActionsheetDragIndicatorWrapper>

            <VStack className="w-full px-5 pb-2 pt-3">
              <Text className="font-semibold text-lg text-typography-900">Message templates</Text>
            </VStack>

            <ActionsheetScrollView
              className="w-full"
              contentContainerClassName="px-4 pb-6"
              showsVerticalScrollIndicator={false}>
              {templateList}
            </ActionsheetScrollView>
          </ActionsheetContent>
        </Actionsheet>
      </>
    );
  }

  return (
    <Popover
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      onOpen={() => setIsOpen(true)}
      placement={isWeb ? 'top' : 'bottom'}
      trigger={(triggerProps: Record<string, unknown>) => trigger(triggerProps)}>
      <PopoverBackdrop />
      <PopoverContent
        size="md"
        className="w-80"
        style={isWeb ? { maxHeight: 360, width: 320 } : { maxHeight: 360 }}>
        <PopoverBody
          contentContainerStyle={{ paddingBottom: 4 }}
          showsVerticalScrollIndicator={false}>
          {templateList}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
});

TemplateAction.displayName = 'TemplateAction';

const TemplateRow = memo(function TemplateRow({
  template,
  variables,
  onInsert,
  isSending,
  isLastInSection,
}: {
  template: MessageTemplate;
  variables: TemplateVariables;
  onInsert: (template: MessageTemplate) => void;
  isSending: boolean;
  isLastInSection: boolean;
}) {
  const preview = interpolateTemplate(template.body, variables);

  return (
    <View>
      <Pressable
        onPress={() => onInsert(template)}
        disabled={isSending}
        className="rounded px-1 py-2 active:bg-background-100 web:hover:bg-background-50"
        {...getAccessibilityProps({ label: template.title, role: 'button' })}>
        <Text className="font-semibold text-base leading-6 text-typography-900">
          {template.title}
        </Text>
        <Text className="text-sm leading-5 text-typography-700" numberOfLines={2}>
          {preview}
        </Text>
      </Pressable>
      {!isLastInSection ? <Divider className="mx-1 bg-outline-200" /> : null}
    </View>
  );
});

TemplateRow.displayName = 'TemplateRow';
